// src/utils/imageLoader.js - Enhanced with priority loading
// Core image loading functionality with caching and priority system

// Image cache to prevent re-loading
const imageCache = new Map();
const MAX_CACHE_SIZE = 100;

// Priority loading queue
class ImageLoadQueue {
  constructor() {
    this.queue = [];
    this.loading = new Set();
    this.maxConcurrent = 4; // Increased for initial load
  }

  add(imagePath, priority = 'normal') {
    if (this.loading.has(imagePath) || imageCache.has(imagePath)) {
      return Promise.resolve(imageCache.get(imagePath));
    }
    
    return new Promise((resolve, reject) => {
      const item = { 
        imagePath, 
        priority, 
        timestamp: Date.now(),
        resolve,
        reject
      };
      
      // Insert based on priority
      if (priority === 'high') {
        this.queue.unshift(item);
      } else {
        this.queue.push(item);
      }
      
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.loading.size >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    const item = this.queue.shift();
    this.loading.add(item.imagePath);

    try {
      const url = await this.loadSingleImage(item.imagePath);
      item.resolve(url);
    } catch (error) {
      item.reject(error);
    } finally {
      this.loading.delete(item.imagePath);
      // Process next in queue
      setTimeout(() => this.processQueue(), 10);
    }
  }

  async loadSingleImage(imagePath) {
    if (imageCache.has(imagePath)) {
      return imageCache.get(imagePath);
    }
    
    try {
      const imageModule = await import(`../images/${imagePath}`);
      const imageUrl = imageModule.default;
      addToCache(imagePath, imageUrl);
      return imageUrl;
    } catch (error) {
      console.error(`Failed to load image: ${imagePath}`, error);
      return null;
    }
  }
}

const imageLoadQueue = new ImageLoadQueue();

// Cache management
const addToCache = (key, value) => {
  if (imageCache.size >= MAX_CACHE_SIZE) {
    // Remove oldest entry (LRU)
    const firstKey = imageCache.keys().next().value;
    imageCache.delete(firstKey);
  }
  imageCache.set(key, value);
};

// Enhanced load function with priority
export const loadImage = async (imagePath, priority = 'normal') => {
  if (imageCache.has(imagePath)) {
    return imageCache.get(imagePath);
  }
  
  return imageLoadQueue.add(imagePath, priority);
};

// Load first few images with high priority (for initial page load)
export const preloadCriticalImages = (imagePaths) => {
  return Promise.all(
    imagePaths.slice(0, 6).map(path => loadImage(path, 'high'))
  );
};

// Preload images without waiting
export const preloadImage = (imagePath, priority = 'normal') => {
  if (!imageCache.has(imagePath)) {
    loadImage(imagePath, priority).catch(() => {
      // Silently handle preload errors
    });
  }
};

// Preload multiple images
export const preloadImages = (imagePaths, delay = 50) => {
  imagePaths.forEach((path, index) => {
    setTimeout(() => {
      preloadImage(path, index < 3 ? 'high' : 'normal');
    }, index * delay);
  });
};

// Clear cache (useful for memory management)
export const clearImageCache = () => {
  imageCache.clear();
};

// Get cache status
export const getCacheStatus = () => {
  return {
    size: imageCache.size,
    maxSize: MAX_CACHE_SIZE,
    keys: Array.from(imageCache.keys()),
    queueSize: imageLoadQueue.queue.length,
    loading: imageLoadQueue.loading.size
  };
};

export default { 
  loadImage, 
  preloadImage, 
  preloadImages, 
  preloadCriticalImages,
  clearImageCache, 
  getCacheStatus 
};